// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Accordion, Box, Group, Modal, Text, Title } from '@mantine/core';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { i18n } from '@lingui/core';
import { IconChevronRight, IconRefresh } from '@tabler/icons';
import { ErrorBoundary } from 'react-error-boundary';
import { useDisclosure } from '@mantine/hooks';
import PanePaper from '../../PanePaper';
import { WorkflowDTO } from '../../../Types/LogT';
import { WorkflowS } from '../../../Service/WorkflowS';
import SkeletonItems from '../../../Atoms/SkeletonItems';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import BookitupThemeIcon from '../../../Atoms/BookitupThemeIcon';
import { EventContext } from '../../../Service/Context/EventContext';
import { ExtendedOnboardingModal } from '../../../Templates/Onboarding/OnboardingModals';
import Workflow from './Workflow';
import { openAddWorkflowModal } from './WorkflowModals';

const WorkflowsPane = () => {
  const { id: eventId } = useParams();
  const { event } = useContext<any>(EventContext);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const [workflows, setWorkflows] = useState<WorkflowDTO[]>([]);
  const [openedWorkflows, setOpenedWorkflows] = useState<string[]>([]);
  const [onboardingOpened, { close, open }] = useDisclosure(false);
  const [onboardingStarted, setOnboardingStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isEmpty = workflows.length === 0;
  const { name: eventName } = event ?? {};

  const onboardingWorkflow = useMemo(
    () => workflows.find((w) => w.name === 'Vertrag 8 Stunden Foto-Reportage'),
    [workflows],
  );

  useEffect(() => {
    if (
      !onboardingStarted &&
      onboardingWorkflow &&
      onboardingWorkflow.executionStatus !== 'COMPLETED' &&
      eventName === 'Onboarding-Event'
    ) {
      open();
      setOnboardingStarted(true);
    }
  }, [onboardingStarted, eventName, onboardingWorkflow, open]);

  useEffect(() => {
    getWorkflows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const getWorkflows = (withDelay = false) => {
    // Enable delay opt to make sure all automatic workflows were already created at the given moment
    if (eventId) {
      setLoading(true);
      setTimeout(
        () => {
          WorkflowS.fetchWorkflows(eventId)
            .then(setWorkflows)
            .finally(() => setLoading(false));
        },
        withDelay ? 2500 : 0,
      );
    }
  };

  if (!eventId) {
    return null;
  }

  const removeWorkflow = (workflow: WorkflowDTO) => setWorkflows(workflows.filter((w) => w.id !== workflow.id));

  const addWorkflow = () => {
    executeWithLicenceCheck(LicenceS.Restrictions.AUTOMATIONS, () => openAddWorkflowModal(getWorkflows, eventId));
  };

  const toggleAccordionItem = (itemId: string, show: boolean) => {
    if (show) {
      setOpenedWorkflows([...openedWorkflows, itemId]);
    } else {
      setOpenedWorkflows(openedWorkflows.filter((owId) => owId !== itemId));
    }
  };

  return (
    <PanePaper
      title={i18n._('event.details.workflows.pane.title')}
      rightSection={
        <Group spacing="xs">
          {!loading && (
            <BookitupThemeIcon onClick={() => getWorkflows(false)}>
              <IconRefresh />
            </BookitupThemeIcon>
          )}
          <BookitupAddIcon onClick={addWorkflow} />
        </Group>
      }
    >
      {loading && <SkeletonItems height={60} />}
      {!loading && isEmpty && (
        <Text italic color="dimmed" size="xs" weight="lighter">
          Noch nichts hinzugefügt.
        </Text>
      )}
      {!loading && !isEmpty && (
        <Accordion
          multiple
          value={openedWorkflows}
          chevronPosition="left"
          chevron={<IconChevronRight size="1rem" />}
          styles={{
            chevron: {
              '&[data-rotate]': {
                transform: 'rotate(90deg)',
              },
            },
          }}
        >
          {workflows.map((workflow) => (
            <ErrorBoundary
              key={workflow.id}
              fallback={
                <Box sx={{ marginTop: 10, marginBottom: 10, marginLeft: 15 }}>
                  <Text size="sm">
                    ⚠️ Oh no! Im Workflow{' '}
                    <Text
                      weight={'bolder'}
                      span
                      component="a"
                      color="blue"
                      onClick={() => navigate(`/settings/workflow/templates/${workflow.templateId}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {workflow.name}
                    </Text>{' '}
                    liegt ein Fehler vor.
                  </Text>
                  <Text size="xs" color="dimmed">
                    Bitte kontaktiere den Support, um das Problem zu beheben.
                  </Text>
                </Box>
              }
            >
              <Workflow
                key={workflow.id}
                previewOpened={openedWorkflows.includes(workflow.id)}
                setShowPreview={(show: boolean) => toggleAccordionItem(workflow.id, show)}
                workflow={workflow}
                onDelete={() => removeWorkflow(workflow)}
              />
            </ErrorBoundary>
          ))}
        </Accordion>
      )}
      {onboardingWorkflow && (
        <Modal
          opened={onboardingOpened}
          size="lg"
          withCloseButton={false}
          closeOnEscape={false}
          transition="slide-down"
          onClose={close}
          title={<Title order={3}>Beispiel-Online-Vertrag für Fotografen</Title>}
          zIndex={201}
        >
          <ExtendedOnboardingModal onboardingWorkflow={onboardingWorkflow} close={close} />
        </Modal>
      )}
    </PanePaper>
  );
};

export default WorkflowsPane;
